import React, { useState } from 'react';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography,
  Divider, IconButton, ListItem, ListItemIcon,
  ListItemText, Avatar, Menu, MenuItem, useMediaQuery,
  Badge, alpha
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Computer as ComputerIcon,
  Assignment as OsIcon,
  Money as MoneyIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  ChevronLeft as ChevronLeftIcon,
  Receipt as AuditIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const drawerWidth = 280;

function Layout({ children, title }) {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const handleDrawerToggle = () => setOpen(!open);
  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    signOut();
    toast.info('Logout realizado com sucesso');
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Clientes', icon: <PeopleIcon />, path: '/clientes' },
    { text: 'Computadores', icon: <ComputerIcon />, path: '/computadores' },
    { text: 'Ordens de Serviço', icon: <OsIcon />, path: '/os' },
    { text: 'Financeiro', icon: <MoneyIcon />, path: '/financeiro' },
    { text: 'Auditoria', icon: <AuditIcon />, path: '/audit-logs' },
    { text: 'Configurações', icon: <SettingsIcon />, path: '/configuracoes' },
  ];

  const getPageTitle = () => {
    const item = menuItems.find(i => i.path === location.pathname);
    return item ? item.text : title || 'Dashboard';
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      {/* AppBar Melhorada */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #0A2647 0%, #1a3b5c 100%)',
          boxShadow: '0 4px 30px rgba(10, 38, 71, 0.15)',
          backdropFilter: 'blur(10px)',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(open && !isMobile && {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }),
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              onClick={handleDrawerToggle}
              edge="start"
              sx={{ 
                mr: 2,
                '&:hover': { 
                  transform: 'rotate(90deg)',
                  transition: 'transform 0.3s ease'
                }
              }}
            >
              {open && !isMobile ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StoreIcon sx={{ color: '#39FF14', fontSize: 28 }} />
              <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
                {getPageTitle()}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              color="inherit"
              sx={{
                '&:hover': {
                  transform: 'translateY(-2px)',
                  transition: 'transform 0.2s ease'
                }
              }}
            >
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <IconButton 
              onClick={handleProfileMenuOpen} 
              color="inherit"
              sx={{
                '&:hover': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s ease'
                }
              }}
            >
              <Avatar 
                sx={{ 
                  bgcolor: '#39FF14', 
                  color: '#0A2647',
                  width: 36, 
                  height: 36,
                  fontWeight: 700
                }}
              >
                {user?.nome?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
            </IconButton>

            <Menu 
              anchorEl={anchorEl} 
              open={Boolean(anchorEl)} 
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  borderRadius: 2,
                  boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
                  minWidth: 180
                }
              }}
            >
              <MenuItem disabled sx={{ py: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {user?.nome || 'Usuário'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.email || ''}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: '#d32f2f' }}>
                <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: '#d32f2f' }} /></ListItemIcon>
                <ListItemText>Sair</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer Melhorado */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={open}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
            borderRight: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '2px 0 20px rgba(0,0,0,0.04)',
          },
        }}
      >
        {/* Header do Drawer */}
        <Box sx={{
          p: 2.5,
          background: 'linear-gradient(135deg, #0A2647 0%, #1a3b5c 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          minHeight: 70
        }}>
          <StoreIcon sx={{ color: '#39FF14', fontSize: 32 }} />
          <Typography variant="h6" sx={{ 
            color: '#ffffff',
            fontWeight: 700,
            letterSpacing: 1
          }}>
            JobsonInfo
          </Typography>
        </Box>
        
        <Divider sx={{ opacity: 0.1 }} />

        {/* Menu Items */}
        <List sx={{ 
          pt: 2, 
          px: 1.5,
          '& .MuiListItem-root': {
            borderRadius: 2,
            mb: 0.5,
            py: 1.2,
            transition: 'all 0.2s ease'
          }
        }}>
          {menuItems.map((item) => {
            const isSelected = location.pathname === item.path ||
                              (item.path === '/dashboard' && location.pathname === '/');

            return (
              <ListItem
                button
                key={item.text}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setOpen(false);
                }}
                selected={isSelected}
                sx={{
                  '&:hover': {
                    background: 'rgba(10, 38, 71, 0.05)',
                    transform: 'translateX(4px)',
                  },
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, rgba(10, 38, 71, 0.12) 0%, rgba(26, 59, 92, 0.08) 100%)',
                    borderRight: '3px solid #0A2647',
                    '& .MuiListItemIcon-root': {
                      color: '#0A2647',
                    },
                    '& .MuiTypography-root': {
                      fontWeight: 600,
                      color: '#0A2647',
                    }
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  color: isSelected ? '#0A2647' : '#666',
                  minWidth: 40,
                  '& svg': { fontSize: 22 }
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ 
                    fontSize: 14, 
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? '#0A2647' : '#333'
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      {/* Conteúdo Principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default Layout;
